import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Search from 'components/shared/Search';
import { useReferralBuildingTerminology } from 'hooks/useReferralBuildingTerminology';
import { ChatConversation } from 'services/api/chat';
import { colors } from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';

import ChannelListItem from './ChannelListItem';

type Props = {
  conversations: ChatConversation[];
  selectedId: string | undefined;
  onSelect: (conversationId: string) => void;
};

// The facility rail (Figma "Multi Facility Access"), shown only when a referral has more
// than one channel. Alphabetical by facility name with a client-side search filter. Rows
// aren't virtualized: the repo has no virtualization primitive and lists render directly
// (the ActivityLog precedent); a referral tops out around ~100 facilities.
export default function ChannelRail({ conversations, selectedId, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const { chatRailHeading } = useReferralBuildingTerminology();

  const sorted = useMemo(
    () => [...conversations].sort((a, b) => a.audience.name.localeCompare(b.audience.name)),
    [conversations]
  );

  // Deliberately a FRONTEND filter, not an API search (decided 2026-08-03): the full
  // policy-scoped channel list is always fetched regardless — the tab badge sums unread
  // across every visible channel — so the complete answer set is already in hand, and a
  // server round-trip per keystroke would add latency and cache complexity while reducing
  // nothing. Switch to an API search only when the list stops being fully fetched (a
  // client exceeding the ~100-facility assumption forces rail pagination) or when search
  // means message CONTENT rather than facility names — both necessarily server-side.
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter((conversation) => conversation.audience.name.toLowerCase().includes(needle));
  }, [sorted, search]);

  return (
    <Rail data-testid='chat-channel-rail'>
      <Heading>{chatRailHeading}</Heading>
      <Search value={search} onChange={setSearch} placeholder='Search Facility' />
      <List>
        {visible.map((conversation) => (
          <ChannelListItem
            key={conversation.id}
            conversation={conversation}
            selected={conversation.id === selectedId}
            onSelect={onSelect}
          />
        ))}
        {visible.length === 0 && <Empty data-testid='chat-rail-empty'>No facilities match “{search}”.</Empty>}
      </List>
    </Rail>
  );
}

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Heading = styled(BodySmallBold)`
  /* Figma: rgba(2, 19, 32, 0.5) — black50 is the closest brand token (same 50% alpha). */
  color: ${colors.black50};
  font-weight: normal;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
`;

const Empty = styled.div`
  padding: 8px 12px;
  color: ${colors.black50};
  font-size: 13px;
`;
